import { Color } from './Color';
import { Z_INDEX_BASE, Z_INDEX_DROPDOWN } from './zIndex';
import { Css, ICustomTheme } from '../ui';
import { FONT_FAMILY_DESYREL, FONT_FAMILY_MUSEO_SANS_CONDENSED } from './Font';
import { AirportCodes } from '../types/airports';
import { getAirportColorTheme } from '../utils/getAirportColorTheme';

export function generateTheme(airportCode?: AirportCodes) {
	const isCorporate = airportCode === AirportCodes.Corporate;
	const colorTheme = airportCode && getAirportColorTheme(airportCode);
	const {
		primary,
		secondary,
		onPrimary,
		onSecondary,
		primaryLight,
		secondaryLighter,
		secondaryLight,
	} = colorTheme || {};

	const theme: ICustomTheme = {
		layout: {
			page: {
				padding: '0',
				background: Color.Neutral1,
			},
			main: {
				textMaxWidth: Css.pxToRem(792),
			},
			image: {
				padding: `0 0 ${Css.pxToRem(24)}`,
				maxWidth: Css.pxToRem(792),
				borderRadius: '16px',
				mobile: {
					padding: `0 0 ${Css.pxToRem(24)}`,
					maxWidth: '100%',
					borderRadius: '8px',
				},
				tablet: {
					maxWidth: '100%',
				},
			},
		},
		background: {
			default: Color.Neutral1,
		},
		callToAction: {
			padding: `${Css.pxToRem(32)}`,
			borderRadius: '8px',
			background: {
				default: Color.Neutral2,
				inverted: primary,
			},
			font: {
				color: {
					default: isCorporate ? Color.OnBackground : onPrimary,
					inverted: onPrimary,
				},
			},
			mobile: {
				padding: `${Css.pxToRem(32)}`,
			},
		},
		color: {
			brand: primary,
			secondary,
			secondaryLight,
			secondaryLighter,
			onSecondary,
			onPrimary,
			primaryLight,
			highlight: secondary,
			background: {
				default: Color.Neutral1,
			},
			text: {
				default: Color.OnBackground,
				inverted: onSecondary,
			},
			state: {
				error: Color.Error,
			},
		},
		grid: {
			gutter: {
				horizontal: {
					default: Css.pxToRem(24),
					mobile: Css.pxToRem(16),
				},
				vertical: {
					default: Css.pxToRem(24),
					mobile: Css.pxToRem(16),
				},
			},
			row: {
				defaultColumns: [2, { tablet: 1 }],
			},
		},
		accordion: {
			title: {
				padding: `${Css.pxToRem(22)} 0 ${Css.pxToRem(30)}`,
				fontFamily: `${FONT_FAMILY_MUSEO_SANS_CONDENSED}, Arial, Roboto, sans-serif`,
			},

			content: {
				padding: `${Css.pxToRem(16)} 0 ${Css.pxToRem(8)}`,
			},
			icon: {
				color: secondaryLight,
			},
		},
		typography: {
			body: {
				fontFamily: `${FONT_FAMILY_MUSEO_SANS_CONDENSED}, Arial, Roboto, sans-serif`,
				size: '16px',
				lineHeight: '1.375', // Unitless line height (1.375 * 16px = 22px)
				color: Color.OnBackground,
				fontWeight: '300',
			},

			paragraph: {
				margin: '0 0 1.5em 0',
			},

			header: {
				margin: '0 0 0.3em 0',
				h1: {
					size: Css.pxToRem(96),
					color: Color.OnBackground,
					fontWeight: '700',
				},
				h2: {
					size: Css.pxToRem(64),
					color: Color.OnBackground,
					fontWeight: '700',
				},
				h3: {
					size: Css.pxToRem(52),
					color: Color.OnBackground,
					fontWeight: '500',
				},
				h4: {
					size: Css.pxToRem(32),
					color: Color.OnBackground,
					fontWeight: '500',
				},
				h5: {
					size: Css.pxToRem(24),
					color: Color.OnBackground,
					fontWeight: '500',
				},
				h6: {
					size: Css.pxToRem(16),
					color: Color.OnBackground,
					fontWeight: 500,
				},
			},
			subheading: {
				font: {
					fontFamily: FONT_FAMILY_DESYREL,
					fontWeight: '400',
				},
			},
			mobile: {
				header: {
					h1: {
						size: Css.pxToRem(72),
					},
					h2: {
						size: Css.pxToRem(54),
					},
					h3: {
						size: Css.pxToRem(52),
					},
					h4: {
						size: Css.pxToRem(32),
					},
					h5: {
						size: Css.pxToRem(24),
					},
					h6: {
						size: Css.pxToRem(16),
					},
				},
			},
			link: {
				hover: {
					color: Color.Secondary,
				},
				color: Color.Secondary,
				fontWeight: '500',
			},
			small: {
				size: Css.pxToRem(14),
				weight: '300',
				color: Color.Neutral6,
			},
		},
		card: {
			boxShadow: 'none',
			contentMargin: `${Css.pxToRem(24)} ${Css.pxToRem(16)}`,
			borderRadius: '8px',
			fullyClickable: {
				boxShadow: 'none',
				hover: {
					boxShadow: 'none',
				},
			},
			icon: {
				color: secondaryLight,
			},
		},
		blockquote: {
			boxShadow: `inset ${Css.pxToRem(5)} 0 0 0 ${Color.Secondary}`,
			paragraph: { color: Color.OnBackground, size: Css.pxToRem(16) },
			padding: `0 ${Css.pxToRem(16)}`,
			margin: '0 0 1.5em 0',
			background: 'transparent',
		},
		alertBanner: {
			background: Color.Tertiary2Light,
			title: {
				fontSize: Css.pxToRem(24),
			},
		},

		button: {
			iconSeparator: '.7em',
			iconSize: Css.pxToRem(22),
			radius: '8px',
			padding: `${Css.pxToRem(17)} ${Css.pxToRem(45)}`,

			primary: {
				background: secondary,
				border: `1px solid ${secondary}`,
				font: {
					color: onSecondary,
					weight: '700',
				},
				hover: {
					background: isCorporate ? Color.OnBackgroundLight : primaryLight,
					border: `1px solid ${primaryLight}`,
					font: {
						color: isCorporate ? Color.OnBackground : onPrimary,
					},
				},
				focus: {
					background: isCorporate ? Color.OnBackgroundLight : primaryLight,
					border: `2px solid ${isCorporate ? Color.BorderFocus : primary}`,
					font: {
						color: isCorporate ? Color.OnBackground : onPrimary,
					},
				},
				active: {
					background: isCorporate
						? Color.ActiveBackgroundLight
						: secondaryLighter,
					boxShadow: 'none',
					border: 'none',
					font: {
						color: isCorporate ? Color.OnBackground : onSecondary,
					},
				},
				disabled: {
					background: Color.DisabledBackground,
					border: `1px solid ${Color.DisabledBackground}`,
					opacity: '1',
					font: {
						color: Color.Neutral6,
					},
				},
				inverted: {
					background: onSecondary,
					border: `2px solid ${onSecondary}`,
					font: {
						color: isCorporate ? Color.OnBackground : onPrimary,
						weight: '700',
					},
					hover: {
						border: `2px solid ${
							isCorporate ? `transparent` : Color.OnBackground
						}`,
						background: isCorporate ? Color.OnBackground : onPrimary,
						font: {
							color: isCorporate ? Color.Neutral1 : onSecondary,
						},
					},
					focus: {
						border: `2px solid ${primaryLight}`,
						background: isCorporate ? Color.OnBackground : onPrimary,
						font: {
							color: onSecondary,
						},
					},
					active: {
						border: `2px solid ${isCorporate ? Color.Tertiary2 : onPrimary}`,
						font: {
							color: isCorporate ? Color.Tertiary2 : primary,
						},
					},
				},
				invertedSecondary: {
					background: onSecondary,
					border: `2px solid ${onSecondary}`,
					font: {
						color: Color.OnBackground,
						weight: '700',
					},
					hover: {
						border: `2px solid ${Color.Tertiary2Dark}`,
						background: Color.Tertiary2Dark,
						font: {
							color: Color.Neutral1,
						},
					},
					focus: {
						border: `2px solid ${Color.Tertiary2Light}`,
						background: Color.Tertiary2Dark,
						font: {
							color: isCorporate ? Color.Neutral1 : Color.Tertiary2Light,
						},
					},
					active: {
						border: `2px solid ${Color.Tertiary2Dark}`,
						boxShadow: 'none',
						font: {
							color: Color.Tertiary2,
						},
					},
				},
			},
			secondary: {
				border: `1px solid ${secondary}`,
				font: {
					color: secondary,
					weight: '700',
				},
				hover: {
					border: isCorporate
						? `2px solid ${secondary}`
						: `1px solid ${secondaryLight}`,
					font: {
						color: secondary,
					},
					background: isCorporate ? Color.OnBackgroundLight : primaryLight,
				},
				focus: {
					border: `2px solid ${isCorporate ? Color.BorderFocus : primary}`,
					background: isCorporate ? Color.OnBackgroundLight : primaryLight,
				},
				active: {
					border: `1px solid ${primary}`,
					background: isCorporate
						? Color.ActiveBackgroundLight
						: secondaryLighter,
					font: {
						color: isCorporate ? Color.OnBackground : onSecondary,
					},
					boxShadow: 'none',
				},
				disabled: {
					border: `1px solid ${Color.Neutral5}`,
					opacity: '1',
					font: {
						color: Color.Neutral5,
					},
				},
				inverted: {
					border: `1px solid ${onPrimary}`,
					font: {
						color: onPrimary,
						weight: '700',
					},
					hover: {
						border: isCorporate ? `1px solid ${Color.OnBackground}` : `none`,
						background: primaryLight,
						font: {
							color: isCorporate ? Color.OnBackground : onPrimary,
						},
					},
					focus: {
						border: `2px solid ${isCorporate ? Color.OnBackground : onPrimary}`,
						background: primaryLight,
						font: {
							color: isCorporate ? Color.OnBackground : onPrimary,
						},
					},
					active: {
						border: isCorporate
							? `2px solid ${secondaryLight}`
							: `1px solid ${primaryLight}`,
						background: primaryLight,
						boxShadow: 'none',
						font: {
							color: isCorporate ? secondaryLight : `inherit`,
						},
					},
				},
				invertedSecondary: {
					border: `1px solid ${Color.OnBackground}`,
					font: {
						color: Color.OnBackground,
						weight: '700',
					},
					hover: {
						border: `1px solid ${Color.Tertiary2Dark}`,
						background: Color.Tertiary2Light,
						font: {
							color: isCorporate ? Color.OnBackground : onPrimary,
						},
					},
					focus: {
						border: `2px solid ${Color.OnBackground}`,
						background: Color.Tertiary2Light,
					},
					active: {
						border: `2px solid ${Color.Tertiary2Light}`,
						boxShadow: 'none',
					},
				},
			},
			textButton: {
				background: 'transparent',
				border: '2px solid transparent',
				font: {
					color: secondary,
					weight: '700',
				},
				hover: {
					border: '2px solid transparent',
					font: {
						color: secondaryLighter,
					},
					background: 'transparent',
				},
				focus: {
					border: `2px solid ${primary}`,
					background: primaryLight,
				},
				active: {
					border: '2px solid transparent',
					background: secondaryLighter,
					font: {
						color: onSecondary,
					},
					boxShadow: 'none',
				},
				disabled: {
					opacity: '1',
					font: {
						color: Color.Neutral5,
					},
				},
			},
		},
		zIndex: {
			base: `${Z_INDEX_BASE}`,
			dropdown: `${Z_INDEX_DROPDOWN}`,
		},
		select: {
			main: {
				background: Color.InputBackground,
				color: Color.OnBackground,
				borderRadius: '8px',
				boxShadow: 'none',
				borderBottom: `2px solid transparent`,
				padding: '14px',
			},

			focus: {
				boxShadow: 'none',
				borderBottom: `2px solid ${primary}`,
				borderRadius: '8px',
			},
		},
		input: {
			padding: Css.pxToRem(14),
			placeholder: {
				color: Color.OnBackground,
				focussed: {
					color: Color.Neutral6,
				},
			},
		},
		dropdownList: {
			option: {
				highlighted: {
					color: primary,
					background: Color.Neutral3,
				},
				selected: {
					background: Color.Neutral3,
				},
				padding: `${Css.pxToRem(12)} ${Css.pxToRem(16)}`,
			},
			options: {
				zIndex: '2',
			},
		},
		border: {
			default: `1px solid ${Color.Border}`,
		},
		shimmer: {
			placeholder: {
				borderRadius: '8px',
				background: Color.Neutral3,
			},
		},
		image: {
			borderRadius: {
				default: '8px',
				mobileXL: '8px',
			},
		},
		checkbox: {
			size: '20px',
			icon: {
				size: '17px',
				color: Color.Neutral1,
			},
		},
	};

	return theme;
}
